import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { getPublicProject } from "../Projects/services/projectCrud";
import { getPublicTree } from "../Trees/services/treeCrud";
import { getTodayWeather, getTommorowWeather } from "@helper/weather";
import useTreeRotation from "./hooks/useSlideRotation";
import SensorInfoSlide from "./components/SensorInfoSlide";
import BiodiversitySlide from "./components/BiodiversitySlide";
import StaudenSlide from "./components/StaudenSlide";
import { getSoilMoistureGaugeColor } from "./utils/soilMoistureColor";
import "./ExternalDisplayV2.scss"; // shared dv2-* classes for LeftPanel + Footer
import "./ExternalDisplayV3.scss"; // dv3-* overrides for slide layout

// =============================================================================
// Helpers (identical to V2)
// =============================================================================

const RIGHT_AUTO_SCROLL_SELECTOR = ".dv3-info-slide, .dv3-info";

function useAutoScroll(ref, resetKey, selector = null) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;

    let frameId = null;
    let startTimer = null;
    const directions = new Map();
    const speedPxPerMs = 0.055;
    const edgePauseMs = 1200;
    const startDelayMs = 700;

    const stop = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
      if (startTimer) {
        clearTimeout(startTimer);
        startTimer = null;
      }
      directions.clear();
    };

    const getTargets = () => {
      if (!selector) return [root];
      return Array.from(root.querySelectorAll(selector));
    };

    const start = () => {
      stop();

      const targets = getTargets();
      targets.forEach((target) => {
        target.scrollTop = 0;
      });

      const scrollables = targets.filter((target) => target.scrollHeight - target.clientHeight > 4);
      if (!scrollables.length) return;

      scrollables.forEach((target) => directions.set(target, 1));
      let lastTimestamp = performance.now();
      let pauseUntil = lastTimestamp + startDelayMs;

      const step = (timestamp) => {
        const delta = Math.min(timestamp - lastTimestamp, 64);
        lastTimestamp = timestamp;

        if (timestamp >= pauseUntil) {
          let reachedEdge = false;

          scrollables.forEach((target) => {
            const maxScroll = target.scrollHeight - target.clientHeight;
            if (maxScroll <= 4) return;

            const direction = directions.get(target) || 1;
            const nextScrollTop = target.scrollTop + direction * speedPxPerMs * delta;

            if (nextScrollTop >= maxScroll) {
              target.scrollTop = maxScroll;
              directions.set(target, -1);
              reachedEdge = true;
            } else if (nextScrollTop <= 0) {
              target.scrollTop = 0;
              directions.set(target, 1);
              reachedEdge = true;
            } else {
              target.scrollTop = nextScrollTop;
            }
          });

          if (reachedEdge) pauseUntil = timestamp + edgePauseMs;
        }

        frameId = requestAnimationFrame(step);
      };

      frameId = requestAnimationFrame(step);
    };

    startTimer = setTimeout(start, 250);
    return stop;
  }, [ref, resetKey, selector]);
}

const WEATHER_ICONS = {
  0: "/img/weather/sun.png",
  1: "/img/weather/partly-sunny.png",
  2: "/img/weather/cloud.png",
  3: "/img/weather/cloud.png",
};

function WeatherIcon({ status, size = 22 }) {
  const src = WEATHER_ICONS[status] ?? WEATHER_ICONS[2];
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      style={{ width: size, height: size, objectFit: "contain" }}
    />
  );
}

function getFallbackTreeImage(species) {
  const name = (species?.de_name || species?.name || "").toLowerCase();
  if (name.includes("amber")) return "/img/trees/amberbaum.png";
  if (name.includes("eisen")) return "/img/trees/eisenholz.png";
  if (name.includes("bluet") || name.includes("blüten")) return "/img/trees/bluetensteppe.png";
  return "/img/trees/amberbaum.png";
}

function isStaudenbeetTree(tree) {
  const speciesKey = (tree?.species?.species || "").toLowerCase();
  const speciesName = (tree?.species?.name || "").toLowerCase();
  const speciesDeName = (tree?.species?.de_name || "").toLowerCase();
  const treeName = (tree?.name || "").toLowerCase();
  const notes = (tree?.notes || "").toLowerCase();

  return (
    speciesKey === "heimische_bluetensteppe" ||
    speciesName.includes("blütensteppe") ||
    speciesName.includes("bluetensteppe") ||
    speciesDeName.includes("blütensteppe") ||
    speciesDeName.includes("bluetensteppe") ||
    treeName.includes("blütensteppe") ||
    treeName.includes("bluetensteppe") ||
    notes.includes("heimische staudenmischung")
  );
}

function mapHealthToRisk(healthState) {
  if (healthState === null || healthState === undefined) return "unknown";
  if (typeof healthState === "number") {
    const map = { 0: "safe", 1: "lrisk", 2: "hrisk", 3: "unknown", 4: "frost" };
    return map[healthState] || "unknown";
  }
  if (typeof healthState !== "string") return "unknown";
  const n = healthState.toLowerCase();
  if (n.includes("good") || n.includes("gut")) return "safe";
  if (n.includes("high") || n.includes("stark")) return "hrisk";
  if (n.includes("medium") || n.includes("mittel")) return "mrisk";
  if (n.includes("low") || n.includes("leicht") || n.includes("dry")) return "lrisk";
  if (n.includes("frost")) return "frost";
  return "unknown";
}

const RISK_CONFIG = {
  safe: { color: "#3DB87A", label: "Gut versorgt", hint: "Der Baum ist\ngut versorgt." },
  lrisk: { color: "#F5C842", label: "Leicht trocken", hint: "Bewässerung\nbald empfohlen." },
  mrisk: { color: "#F0883E", label: "Trockenheit", hint: "Bewässerung\nheute empfohlen." },
  hrisk: { color: "#E05050", label: "Stark trocken", hint: "Akuter\nWasserbedarf." },
  frost: { color: "#2569A3", label: "Frostgefahr", hint: "Frostwarnung\naktiv." },
  unknown: { color: "#8AABBE", label: "Kein Status", hint: "Daten werden\ngeladen." },
};

const ROOT_SYSTEM_LABELS = {
  DE: "Tiefwurzler",
  HE: "Herzwurzler",
  SH: "Flachwurzler",
  UN: null,
};

// Hardcoded Steckbriefe from MGS PDF documentation (2026-04-27_Steckbriefe_Bäume+Stauden.pdf)
const MGS_STECKBRIEFE = {
  parrotia_persica: {
    name: "Persisches Eisenholz",
    latin: "Parrotia persica",
    family: "Zaubernussgewächse (Hamamelidaceae)",
    size: "6 bis 10 (bis 12) m hoch und breit",
    root: "Flachwurzler",
    origin: "Iran bis Aserbaidschan",
    spreading:
      "Der Baum ist hitzeverträglich und trockenheitsresistent, daher wird der Eisenholzbaum gern als Klimabaum empfohlen.",
    leafShape: "eiförmig, glatter Rand, wechselständig",
    heyday: "März",
    location: "Sonne",
    soil: "durchlässige Böden",
    ph: "schwach sauer - schwach alkalisch",
    besonderheit: "leuchtend gelbe bis rote Herbstfärbung",
  },
  liquidambar_styraciflua: {
    name: "Amberbaum",
    latin: "Liquidambar styraciflua",
    family: "Zaubernussgewächse (Hamamelidaceae)",
    size: "10 m hoch und 5 bis 8 m breit",
    root: "Herzwurzler, die tiefen Wurzeln gehen nicht über den Kronenbereich hinaus",
    origin: "Nord- und Mittelamerika",
    spreading:
      "Der Baum ist in Mitteleuropa weit verbreitet, da er hitzeverträglich und wärmeliebend ist.",
    leafShape: "gelappt, gesägter Rand, wechselständig",
    heyday: "Mai",
    location: "Sonne",
    soil: "sandig - lehmig bis lehmig",
    ph: "neutral bis sauer",
    besonderheit: "leuchtend gelbe bis rote Herbstfärbung",
  },
  heimische_bluetensteppe: {
    name: "Heimische Blütensteppe",
    latin: null,
    family: null,
    size: "40 bis 60 cm hoch",
    root: null,
    origin: "Mitteldeutscher Raum",
    spreading:
      "Die Mischung besteht vollständig aus Arten, die alle im mitteldeutschen Raum heimisch sind.",
    leafShape: null,
    heyday: "Frühjahr bis Herbst",
    location: "Sonne",
    soil: "leicht sowie durchlässige Böden, auch auf Kalkschotter",
    besonderheit:
      "heimische Staudenmischung mit u.a. 18 heimischen Arten, Blütenfarben: blau, gelb, rosa, violett",
  },
};

const MGS_STECKBRIEF_ALIASES = {
  amberbaum: "liquidambar_styraciflua",
  amberbaum_1: "liquidambar_styraciflua",
  eisenholz_1: "parrotia_persica",
  eisenholzbaum: "parrotia_persica",
  heimische_bluetensteppe: "heimische_bluetensteppe",
  heimische_blutensteppe: "heimische_bluetensteppe",
  liquidambar_styraciflua: "liquidambar_styraciflua",
  parrotia_persica: "parrotia_persica",
  persisches_eisenholz: "parrotia_persica",
  blutensteppe: "heimische_bluetensteppe",
  bluetensteppe: "heimische_bluetensteppe",
};

function normalizeSteckbriefKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function resolveMgsSteckbrief(treeName, species) {
  const candidates = [species?.species, species?.id, species?.name, species?.de_name, treeName];

  for (const candidate of candidates) {
    const key = normalizeSteckbriefKey(candidate);
    const exactKey = MGS_STECKBRIEF_ALIASES[key] || key;
    if (MGS_STECKBRIEFE[exactKey]) return MGS_STECKBRIEFE[exactKey];

    if (key.includes("eisenholz")) return MGS_STECKBRIEFE.parrotia_persica;
    if (key.includes("amberbaum") || key.includes("liquidambar")) {
      return MGS_STECKBRIEFE.liquidambar_styraciflua;
    }
    if (key.includes("blutensteppe") || key.includes("bluetensteppe")) {
      return MGS_STECKBRIEFE.heimische_bluetensteppe;
    }
  }

  return null;
}

function buildSteckbriefItems(treeName, species) {
  const steckbrief = resolveMgsSteckbrief(treeName, species);

  if (steckbrief) {
    // Use hardcoded MGS data from PDF
    const items = [];
    if (steckbrief.name) items.push({ label: "Name", value: steckbrief.name });
    if (steckbrief.latin) items.push({ label: "Botanisch", value: steckbrief.latin });
    if (steckbrief.family) items.push({ label: "Familie", value: steckbrief.family });
    if (steckbrief.size) items.push({ label: "Größe", value: steckbrief.size });
    if (steckbrief.root) items.push({ label: "Wurzel", value: steckbrief.root });
    if (steckbrief.origin) items.push({ label: "Herkunft", value: steckbrief.origin });
    if (steckbrief.spreading) items.push({ label: "Verbreitung", value: steckbrief.spreading });
    if (steckbrief.besonderheit)
      items.push({ label: "Besonderheit", value: steckbrief.besonderheit });
    if (steckbrief.leafShape) items.push({ label: "Blattform", value: steckbrief.leafShape });
    if (steckbrief.soil) items.push({ label: "Boden", value: steckbrief.soil });
    return items.length > 0 ? items : null;
  }

  if (!species) return null;

  // Fallback to dynamic DB fields for non-MGS trees
  const items = [];

  // Name (German and Latin)
  const deName = species.de_name || species.name;
  const latinName = species.name !== species.de_name ? species.name : null;
  if (deName) items.push({ label: "Name", value: deName });
  if (latinName) items.push({ label: "Botanisch", value: latinName });

  // Family
  if (species.family) items.push({ label: "Familie", value: species.family });

  // Height + Width
  const hasH = species.min_height > 0 || species.max_height > 0;
  const hasW = species.min_width > 0 || species.max_width > 0;
  if (hasH) {
    const h =
      species.min_height && species.max_height
        ? `${species.min_height} bis ${species.max_height} m`
        : `${species.max_height || species.min_height} m`;
    items.push({ label: "Größe", value: `${h} hoch${hasW ? " und breit" : ""}` });
  }

  // Root system
  const rootLabel = ROOT_SYSTEM_LABELS[species.root_system];
  if (rootLabel) items.push({ label: "Wurzel", value: rootLabel });

  // Origin
  if (species.origin) items.push({ label: "Herkunft", value: species.origin });

  // Distribution
  if (species.spreading) items.push({ label: "Verbreitung", value: species.spreading });

  // Special features
  if (species.special_features) {
    items.push({ label: "Besonderheit", value: species.special_features });
  }

  // Leaf shape
  if (species.leaf_shape) items.push({ label: "Blattform", value: species.leaf_shape });

  // Soil
  if (species.soil_type) items.push({ label: "Boden", value: species.soil_type });

  return items.length > 0 ? items : null;
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const days = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  return `${days[d.getDay()]}, ${String(d.getDate()).padStart(2, "0")}.${String(
    d.getMonth() + 1
  ).padStart(2, "0")}.${d.getFullYear()}`;
}

function getTaskDate(task) {
  return task?.scheduledAt || task?.scheduled_at || task?.date || task?.timestamp || null;
}

function parseTaskDate(task) {
  const date = getTaskDate(task);
  if (!date) return null;
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isCompletedTask(task) {
  const status = String(task?.status || "")
    .trim()
    .toLowerCase();
  return ["done", "completed", "erledigt"].includes(status);
}

function getTaskLabel(task) {
  if (!task) return null;
  if (task.measureType) return task.measureType;
  if (task.measure_type) return task.measure_type;
  if (task.title) return task.title;
  if (Array.isArray(task.maintenanceTaskNames) && task.maintenanceTaskNames.length) {
    return task.maintenanceTaskNames.join(", ");
  }
  return "Aufgabe";
}

function getTaskOverview(tree) {
  const tasks = Array.isArray(tree?.tasks) ? tree.tasks : [];
  const now = new Date();
  const datedTasks = tasks
    .map((task) => ({ task, date: parseTaskDate(task) }))
    .filter((item) => item.date);

  const completedTasks = datedTasks.filter(({ task }) => isCompletedTask(task));
  const pastTasks = datedTasks.filter(({ date }) => date <= now);
  const futureTasks = datedTasks.filter(({ task, date }) => !isCompletedTask(task) && date > now);

  const last =
    [...completedTasks].sort((a, b) => b.date - a.date)[0]?.task ||
    [...pastTasks].sort((a, b) => b.date - a.date)[0]?.task ||
    null;
  const next = [...futureTasks].sort((a, b) => a.date - b.date)[0]?.task || null;

  return { last, next };
}

function getSensorInfo(tree) {
  const inst = tree?.installations?.[0];
  const s = inst?.sensor;
  if (!s) return { label: "Messung offline", type: "crown" };
  if (s.soil_moisture_sensor || s.soil_water_tension_sensor)
    return { label: "FDR-Bodensensor aktiv", type: "soil" };
  return { label: "Kronensensor aktiv", type: "crown" };
}

function getMetrics(tree) {
  const inst = tree?.installations?.[0];
  const s = inst?.sensor;
  const species = tree?.species;
  let moistureVal = null;
  let moistureLabel = "Kronenfeuchtigkeit";
  let moistureColor = null;
  const hasUnknownSoilState = s?.soil_moisture_sensor?.soil_state === "UN";
  const hasUnknownSensorState = s?.sensor_state === 3;
  const hasUnknownTreeState = tree?.health_state === 3;

  if (!hasUnknownSensorState && !hasUnknownTreeState && s?.pulse_sensor?.moisture_content != null) {
    moistureVal = Math.round(s.pulse_sensor.moisture_content);
  } else if (
    !hasUnknownSensorState &&
    !hasUnknownTreeState &&
    !hasUnknownSoilState &&
    s?.soil_moisture_sensor?.soil_moisture != null
  ) {
    moistureVal = Math.round(s.soil_moisture_sensor.soil_moisture);
    moistureLabel = "Bodenfeuchte";
    moistureColor = getSoilMoistureGaugeColor(tree, moistureVal);
  } else if (!hasUnknownTreeState && tree?.moisture_content != null) {
    moistureVal = Math.round(tree.moisture_content);
  }

  const tempVal =
    s?.pulse_sensor?.temperature != null
      ? Math.round(s.pulse_sensor.temperature)
      : s?.soil_moisture_sensor?.soil_temperature != null
      ? Math.round(s.soil_moisture_sensor.soil_temperature)
      : null;

  return {
    moistureVal,
    moistureLabel,
    moistureColor,
    tempVal,
    waterReq: species?.water_requirement_per_day || null,
  };
}

// =============================================================================
// Left panel (reuses dv2-* classes from V2 SCSS)
// =============================================================================

function LeftPanel({ project }) {
  const leftPanelRef = useRef(null);
  useAutoScroll(leftPanelRef, project?.id || "project-description");

  const projectTitle = "Aktivierung der Friedrichshafener Straße";
  const projectDescriptionSections = [
    {
      type: "heading",
      text: "Nachbarschaft, Klima und Stadtentwicklung am Westkreuz",
    },
    {
      type: "text",
      text: "Die bisher ungenutzte Fläche an der Friedrichshafener Straße wird bis zur künftigen Nutzung als Baustelleneinrichtung für die U5-Verlängerung nach Freiham ab 2029 zwischengenutzt. Als Teil der Stadtsanierung im Programm „Sozialer Zusammenhalt“ schafft sie wohnortnahe Orte für Begegnung, Erholung und Klimaanpassung – besonders für ältere Menschen, Familien und Personen mit eingeschränkter Mobilität.",
    },
    { type: "heading", text: "Gärtnern und Biodiversität" },
    {
      type: "text",
      text: "Zwei barrierefreie Hochbeete werden vom SeniorenWohnen BRK, dem Seniorentreff am Westkreuz und dem Kindergarten St. Lukas gepflegt. Staudenflächen und ein Gemeinschaftsgarten von Green City e. V. fördern Biodiversität, Stadtklima und nachbarschaftliches Miteinander.",
    },
    { type: "heading", text: "Aufenthalt und Klimaanpassung" },
    {
      type: "text",
      text: "Eine altersgerechte Sitzbank mit Verschattung verbessert die Aufenthaltsqualität und unterstützt die Fußmobilität. Fünf klimaresiliente Bäume wurden gepflanzt und bleiben dauerhaft erhalten.",
    },
    { type: "heading", text: "Sensoren als Reallabor" },
    {
      type: "text",
      text: "Baum- und Bodensensoren erfassen täglich Bodenfeuchte und Baumgesundheit. So können Pflege und Bewässerung gezielt verbessert und Ressourcen effizient eingesetzt werden. Die Daten machen Klimaanpassung transparent und erlebbar.",
    },
    { type: "heading", text: "Begegnung im „Solarium“" },
    {
      type: "text",
      text: "Der rote Überseecontainer „Solarium“ von MAGs e. V. bietet Raum für Begegnung, Aktionen und gemeinsame Programmgestaltung durch Bewohner*innen und Einrichtungen.",
    },
    { type: "heading", text: "Fragen zum Projekt?" },
    {
      type: "text",
      text: "Während der Öffnungszeiten (siehe Eingangstür) hilft das Stadtteilmanagement direkt weiter. Außerhalb erreichen Sie uns telefonisch oder per E-Mail:",
    },
    { type: "contact", text: "Tel.: 089 / 18 92 92 40\nMail: info@neuaubing-westkreuz.de" },
  ];

  const weather = project?.weather;
  const todayStatus = weather ? getTodayWeather(weather) : 2;
  const tomorrowStatus = weather ? getTommorowWeather(weather) : 2;

  return (
    <div className="dv2-left">
      <div className="dv3-project-scroll" ref={leftPanelRef}>
        <div className="dv2-projekt-box dv2-projekt-box--main">
          <div className="dv3-project-header">
            <div className="dv2-hero-title dv3-project-title">{projectTitle}</div>
            <div className="dv2-weather-box dv3-weather-box">
              <div className="dv2-weather-item">
                <WeatherIcon status={todayStatus} size={24} />
                <div className="dv2-weather-content">
                  <div className="dv2-weather-val">
                    {weather?.temperature?.today != null ? `${weather.temperature.today} °C` : "–"}
                  </div>
                  <div className="dv2-weather-lbl">Heute</div>
                </div>
              </div>
              <div className="dv2-weather-item">
                <WeatherIcon status={tomorrowStatus} size={24} />
                <div className="dv2-weather-content">
                  <div className="dv2-weather-val">
                    {weather?.temperature?.tomorrow != null
                      ? `${weather.temperature.tomorrow} °C`
                      : "–"}
                  </div>
                  <div className="dv2-weather-lbl">Morgen</div>
                </div>
              </div>
              <div className="dv2-weather-item">
                <img
                  src="/img/weather/watering.png"
                  alt=""
                  aria-hidden="true"
                  style={{ width: 24, height: 24, objectFit: "contain" }}
                />
                <div className="dv2-weather-content">
                  <div className="dv2-weather-val">
                    {weather?.rain?.today != null ? `${weather.rain.today} mm` : "–"}
                  </div>
                  <div className="dv2-weather-lbl">Niederschlag</div>
                </div>
              </div>
            </div>
          </div>
          {projectDescriptionSections.map((section, index) => (
            <p
              key={`${index}-${section.text.slice(0, 24)}`}
              className={`dv3-project-copy dv3-project-copy--${section.type}`}
            >
              {section.text}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Center visual: alternating tree image ↔ 3D model (toggles every 8 seconds)
// =============================================================================

function CenterVisual({ tree, expectedTreeId }) {
  const [showModel, setShowModel] = useState(false);
  const species = tree?.species;

  // Only trust tree data if it matches the currently selected tree.
  // keepPreviousData causes stale tree objects to linger during fetches.
  const isStale = tree?.id !== expectedTreeId;
  const modelUrl = !isStale ? tree?.three_d_model_url?.trim() || null : null;
  const hasModel = !!modelUrl;
  const viewerUrl = hasModel
    ? `/darbots-viewer/viewer.html?model=${encodeURIComponent(
        modelUrl
      )}&autoRotate=true&autoRotateSpeed=1.5`
    : null;
  const treeImage = getFallbackTreeImage(!isStale ? species : null);

  // Toggle between photo and 3D every 8 seconds (always run, but only show 3D if model exists)
  useEffect(() => {
    const timer = setInterval(() => {
      setShowModel((prev) => !prev);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  // Reset to photo when tree changes
  useEffect(() => {
    setShowModel(false);
  }, [expectedTreeId]);

  // Actual visibility based on both state and model availability
  const actuallyShowModel = showModel && hasModel;

  return (
    <div className="dv3-center">
      {/* Tree image layer */}
      <div className="dv3-center__layer" style={{ opacity: actuallyShowModel ? 0 : 1 }}>
        <img src={treeImage} alt={species?.de_name || "Baum"} className="dv3-center__image" />
      </div>

      {/* 3D model layer (only rendered if URL exists) */}
      {hasModel && (
        <div className="dv3-center__layer" style={{ opacity: actuallyShowModel ? 1 : 0 }}>
          <iframe
            title="3D Baummodell"
            src={viewerUrl}
            className="dv3-center__iframe"
            loading="lazy"
          />
        </div>
      )}

      {/* View indicator buttons */}
      <div className="dv3-center__indicator">
        <button
          type="button"
          className={`dv3-center__tab ${!actuallyShowModel ? "dv3-center__tab--active" : ""}`}
          onClick={() => setShowModel(false)}
        >
          Foto
        </button>
        {hasModel && (
          <button
            type="button"
            className={`dv3-center__tab ${actuallyShowModel ? "dv3-center__tab--active" : ""}`}
            onClick={() => setShowModel(true)}
          >
            3D-Modell
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Info panel (right side) — all tree data
// =============================================================================

function InfoPanel({ tree }) {
  const species = tree?.species;
  const treeName = tree?.name || species?.de_name || species?.name || "Baum";
  const { last, next } = getTaskOverview(tree);

  const steckbriefItems = buildSteckbriefItems(treeName, species);

  return (
    <div className="dv3-info">
      {/* Steckbrief */}
      {steckbriefItems ? (
        <div className="dv3-steckbrief">
          {steckbriefItems.map((item, i) => (
            <div key={i} className="dv3-steckbrief__row">
              <span className="dv3-steckbrief__label">{item.label}</span>
              <span className="dv3-steckbrief__value">{item.value}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="dv3-info__steckbrief">Dieser Baum wird regelmäßig überwacht.</div>
      )}

      {/* Pflege */}
      <div className="dv3-pflege">
        <div className="dv3-pflege__card">
          <div className="dv3-pflege__lbl">Letzte Aufgabe</div>
          <div className="dv3-pflege__val">{formatDate(getTaskDate(last)) || "—"}</div>
          {last ? <div className="dv3-pflege__meta">{getTaskLabel(last)}</div> : null}
        </div>
        <div className="dv3-pflege__card">
          <div className="dv3-pflege__lbl">Nächste Aufgabe</div>
          <div className="dv3-pflege__val">{formatDate(getTaskDate(next)) || "—"}</div>
          {next ? <div className="dv3-pflege__meta">{getTaskLabel(next)}</div> : null}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Slide (restructured: center visual + right info)
// =============================================================================

function Slide({ tree, expectedTreeId, slideIndex, totalSlides, progressPct, onGoTo }) {
  const species = tree?.species;
  const treeName = tree?.name || species?.de_name || species?.name || "Baum";
  const treeLatin = species?.name && species?.name !== treeName ? species.name : null;
  const sensorInfo = getSensorInfo(tree);
  const risk = mapHealthToRisk(tree?.health_state);
  const riskCfg = RISK_CONFIG[risk];
  const { moistureVal, moistureLabel, moistureColor } = getMetrics(tree);

  const moisturePct = moistureVal != null ? Math.max(0, Math.min(100, moistureVal)) : 0;
  const circumference = 2 * Math.PI * 48;
  const moistureGaugeOffset = circumference * (1 - moisturePct / 100);
  const moistureGaugeColor = moistureColor || riskCfg.color;
  const waterReqValue = "100-200l";

  return (
    <div className={`dv3-slide dv3-slide--${risk}`}>
      <div className="dv3-slide__header">
        <div className={`dv2-sensor-badge dv2-sensor-badge--${sensorInfo.type}`}>
          <span className="dv2-s-dot" />
          {sensorInfo.label}
        </div>
        <div className="dv2-nav-dots">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`dv2-nd ${i === slideIndex ? "dv2-nd--on" : ""}`}
              onClick={() => onGoTo(i)}
              aria-label={`Folie ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Tree name + Metrics circles */}
      <div className="dv3-slide__title-row">
        <div className="dv3-slide__title-content">
          <div className="dv3-slide__tree-name">{treeName}</div>
          {treeLatin && (
            <div className="dv3-slide__tree-species">
              <em>{treeLatin}</em> · {sensorInfo.label}
            </div>
          )}
        </div>

        {/* Two circles: Feuchtigkeit + Wasserbedarf */}
        <div className="dv3-slide__metrics-circles">
          {/* Feuchtigkeits-Circle with status label */}
          <div className="dv3-circle">
            <svg width="100" height="100" viewBox="0 0 116 116">
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke="rgba(14,29,44,0.08)"
                strokeWidth="9"
              />
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke={moistureGaugeColor}
                strokeWidth="9"
                strokeDasharray={circumference}
                strokeDashoffset={moistureGaugeOffset}
                strokeLinecap="round"
                transform="rotate(-90 58 58)"
                style={{ transition: "stroke-dashoffset 0.6s ease" }}
              />
              <text
                x="58"
                y="50"
                textAnchor="middle"
                fill="#0E1D2C"
                fontFamily="Syne,sans-serif"
                fontSize="22"
                fontWeight="700"
              >
                {moistureVal != null ? `${moistureVal}%` : "–"}
              </text>
              <text x="58" y="66" textAnchor="middle" fill="#6B8FA4" fontSize="9">
                {moistureLabel}
              </text>
              <text
                x="58"
                y="78"
                textAnchor="middle"
                fill={moistureGaugeColor}
                fontSize="10"
                fontWeight="600"
              >
                {riskCfg.label}
              </text>
            </svg>
          </div>

          {/* Wasserbedarf-Circle */}
          <div className="dv3-circle">
            <svg width="100" height="100" viewBox="0 0 116 116">
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke="rgba(14,29,44,0.08)"
                strokeWidth="9"
              />
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke={riskCfg.color}
                strokeWidth="9"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * 0.3}
                strokeLinecap="round"
                transform="rotate(-90 58 58)"
              />
              <text
                x="58"
                y="56"
                textAnchor="middle"
                fill="#0E1D2C"
                fontFamily="Syne,sans-serif"
                fontSize="18"
                fontWeight="700"
              >
                {waterReqValue}
              </text>
              <text x="58" y="72" textAnchor="middle" fill="#6B8FA4" fontSize="8">
                Bewässerungsbedarf
              </text>
            </svg>
          </div>
        </div>
      </div>

      <div className="dv3-slide__body">
        <CenterVisual tree={tree} expectedTreeId={expectedTreeId} />
        <InfoPanel tree={tree} />
      </div>

      <div className="dv2-prog-bar" style={{ width: `${progressPct}%` }} />
    </div>
  );
}

// =============================================================================
// Footer (identical to V2)
// =============================================================================

function FooterLogo({ src, alt, className = "dv3-flogo__img" }) {
  const [failed, setFailed] = React.useState(false);
  if (failed || !src) return <span className="dv3-flogo__text">{alt}</span>;
  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />;
}

function Footer() {
  return (
    <footer className="dv3-footer">
      <div className="dv3-footer__left">
        <div className="dv3-footer__funders">
          <div className="dv3-flogo dv3-flogo--bund">
            <FooterLogo
              src="/img/logos/bundesministerium.png"
              alt="Bundesministerium für Wohnen, Stadtentwicklung und Bauwesen"
            />
          </div>
          <div className="dv3-flogo dv3-flogo--bayern">
            <FooterLogo
              src="/img/logos/bayern-staatsministerium.png"
              alt="Bayerisches Staatsministerium für Wohnen, Bau und Verkehr"
            />
          </div>
        </div>
      </div>

      <div className="dv3-footer__right">
        <div className="dv3-flogo dv3-flogo--darbots">
          <FooterLogo src="/img/logos/darbots.png" alt="Darbots" className="dv3-flogo__img" />
        </div>
        <div className="dv3-flogo dv3-flogo--treesense">
          <FooterLogo
            src="/img/treesense-logo-colored.webp"
            alt="TreeSense"
            className="dv3-flogo__img"
          />
        </div>
        <div className="dv3-flogo dv3-flogo--mgs">
          <FooterLogo src="/img/logos/mgs.png" alt="MGS" />
        </div>
      </div>
    </footer>
  );
}

// =============================================================================
// Main component
// =============================================================================

export default function ExternalDisplayV3() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const interval = parseInt(searchParams.get("interval") || "12000", 10);
  const rightPanelRef = useRef(null);

  const { data: project, isLoading: projectLoading } = useQuery(
    ["display-v3", "project", projectId],
    () => getPublicProject(projectId),
    {
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      retry: 3,
      retryDelay: 10000,
    }
  );

  const trees = useMemo(() => {
    if (!project?.areas) return [];
    return project.areas.flatMap((area) => area.tree_set || []);
  }, [project]);

  // Build slide configuration: sensor info + trees + biodiversity + stauden
  // Each tree slide internally toggles between photo and 3D
  const slides = useMemo(() => {
    const slideArray = [];
    const regularTrees = [];
    let staudenTree = null;

    // 1. Sensor info slide
    slideArray.push({ type: "sensor-info" });

    // Separate regular trees from Blütensteppe
    trees.forEach((tree) => {
      if (isStaudenbeetTree(tree)) {
        staudenTree = tree;
      } else {
        regularTrees.push(tree);
      }
    });

    // 2-N. Regular tree slides (toggles between photo and 3D internally)
    regularTrees.forEach((tree) => {
      slideArray.push({ type: "tree", treeId: tree.id });
    });

    // Add both biodiversity slides only when a stauden area exists
    if (staudenTree) {
      slideArray.push({ type: "biodiversity", treeId: staudenTree.id });
      slideArray.push({ type: "stauden", treeId: staudenTree.id });
    }

    return slideArray;
  }, [trees]);

  const totalSlides = slides.length || 1;
  const { currentTreeIndex: currentSlideIndex, goToIndex } = useTreeRotation(totalSlides, interval);
  const currentSlide = slides[currentSlideIndex] || { type: "sensor-info" };

  const currentTreeId =
    currentSlide.type === "tree" ||
    currentSlide.type === "stauden" ||
    currentSlide.type === "biodiversity"
      ? currentSlide.treeId
      : null;

  const { data: tree } = useQuery(
    ["display-v3", "tree", currentTreeId],
    () => getPublicTree(currentTreeId),
    {
      enabled: !!currentTreeId,
      refetchInterval: 5 * 60 * 1000,
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      retry: 3,
      retryDelay: 10000,
    }
  );
  const autoScrollKey = `${currentSlideIndex}-${currentSlide.type}-${
    tree?.id || currentTreeId || "static"
  }`;

  const [progressPct, setProgressPct] = useState(0);
  useEffect(() => {
    setProgressPct(0);
    const start = Date.now();
    const timer = setInterval(() => {
      const pct = Math.min(((Date.now() - start) / interval) * 100, 100);
      setProgressPct(pct);
      if (pct >= 100) clearInterval(timer);
    }, 50);
    return () => clearInterval(timer);
  }, [currentSlideIndex, interval]);
  useAutoScroll(rightPanelRef, autoScrollKey, RIGHT_AUTO_SCROLL_SELECTOR);

  if (!project && projectLoading) {
    return <div className="dv2-loading">Daten werden geladen…</div>;
  }

  // Render different slide types
  const renderSlide = () => {
    if (currentSlide.type === "sensor-info") {
      return (
        <SensorInfoSlide
          project={project}
          slideIndex={currentSlideIndex}
          totalSlides={totalSlides}
          progressPct={progressPct}
          onGoTo={goToIndex}
        />
      );
    }

    if (currentSlide.type === "tree") {
      return (
        <Slide
          tree={tree}
          expectedTreeId={currentTreeId}
          slideIndex={currentSlideIndex}
          totalSlides={totalSlides}
          progressPct={progressPct}
          onGoTo={goToIndex}
        />
      );
    }

    if (currentSlide.type === "biodiversity") {
      return (
        <BiodiversitySlide
          tree={tree}
          sensorInfo={getSensorInfo(tree)}
          slideIndex={currentSlideIndex}
          totalSlides={totalSlides}
          progressPct={progressPct}
          onGoTo={goToIndex}
        />
      );
    }

    if (currentSlide.type === "stauden") {
      return (
        <StaudenSlide
          tree={tree}
          sensorInfo={getSensorInfo(tree)}
          slideIndex={currentSlideIndex}
          totalSlides={totalSlides}
          progressPct={progressPct}
          onGoTo={goToIndex}
        />
      );
    }

    return null;
  };

  return (
    <div className="dv2-screen">
      {/* Top bar: institutional logos matching MGS requirements */}
      <div className="dv3-topbar">
        <div className="dv3-topbar__left">
          <img
            src="/img/logos/staedtebau.png"
            alt="Städtebauförderung"
            className="dv3-topbar__logo dv3-topbar__logo--staedtebau"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
        <div className="dv3-topbar__text">
          <span>Dieses Projekt wird im Städtebauförderungsprogramm</span>
          <span>„Sozialer Zusammenhalt“ mit Mitteln des Bundes</span>
          <span>und des Freistaats Bayern gefördert.</span>
        </div>
        <div className="dv3-topbar__right">
          <img
            src="/img/logos/muenchen-referat.png"
            alt="Landeshauptstadt München – Referat für Stadtplanung und Bauordnung"
            className="dv3-topbar__logo dv3-topbar__logo--referat"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      </div>

      <div className="dv2-main">
        <LeftPanel project={project} />

        <div className="dv2-right" ref={rightPanelRef}>
          {renderSlide()}
        </div>
      </div>

      <Footer />
    </div>
  );
}
